import React, { useEffect, useState } from 'react';
import { getPagos, createPago, getPrestamos, getReciboPdfUrl } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Receipt, Download, Search, Printer } from 'lucide-react';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 2,
    }).format(value);
};

export default function PagosPage() {
    const [pagos, setPagos] = useState([]);
    const [prestamos, setPrestamos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [selectedPrestamo, setSelectedPrestamo] = useState(null);
    const [form, setForm] = useState({
        prestamo_id: '',
        monto: '',
        forma_pago: 'efectivo',
        notas: '',
    });

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        try {
            const [pagosRes, prestamosRes] = await Promise.all([
                getPagos(),
                getPrestamos()
            ]);
            setPagos(pagosRes.data);
            setPrestamos(prestamosRes.data);
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handlePrestamoSelect = (prestamoId) => {
        const prestamo = prestamos.find(p => p.id === prestamoId);
        setSelectedPrestamo(prestamo);
        setForm({ ...form, prestamo_id: prestamoId });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...form,
                monto: parseFloat(form.monto),
            };
            await createPago(data);
            toast.success('Pago registrado exitosamente');
            setDialogOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al registrar pago');
        }
    };

    const resetForm = () => {
        setSelectedPrestamo(null);
        setForm({
            prestamo_id: '',
            monto: '',
            forma_pago: 'efectivo',
            notas: '',
        });
    };

    const downloadRecibo = (pagoId) => {
        const token = localStorage.getItem('token');
        const url = getReciboPdfUrl(pagoId);
        window.open(`${url}?token=${token}`, '_blank');
    };

    const printRecibo = (pagoId) => {
        const token = localStorage.getItem('token');
        const url = getReciboPdfUrl(pagoId);
        
        // Abrir PDF en nueva ventana para imprimir
        fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        })
        .then(response => response.blob())
        .then(blob => {
            const blobUrl = URL.createObjectURL(blob);
            const printWindow = window.open(blobUrl, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    setTimeout(() => {
                        printWindow.print();
                    }, 500);
                };
            }
        })
        .catch(error => {
            console.error('Error al imprimir:', error);
            toast.error('Error al preparar el recibo para impresión');
        });
    };

    // Get next pending amount
    const getMinimoPago = () => {
        if (!selectedPrestamo) return 0;
        const pendientes = selectedPrestamo.cuotas.filter(c => c.estado !== 'pagada');
        if (pendientes.length === 0) return 0;
        const cuota = pendientes[0];
        return cuota.total - cuota.monto_pagado + (cuota.mora || 0);
    };

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    const prestamosActivos = prestamos.filter(p => p.estado !== 'cancelado');

    return (
        <div className="space-y-6" data-testid="pagos-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold">Pagos</h1>
                    <p className="text-muted-foreground mt-1">{pagos.length} pagos registrados</p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid="new-pago-btn">
                            <Plus className="w-4 h-4 mr-2" />
                            Registrar Pago
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Registrar Pago</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="prestamo">Préstamo</Label>
                                <Select
                                    value={form.prestamo_id}
                                    onValueChange={handlePrestamoSelect}
                                >
                                    <SelectTrigger data-testid="pago-prestamo-select">
                                        <SelectValue placeholder="Seleccionar préstamo" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {prestamosActivos.map((prestamo) => (
                                            <SelectItem key={prestamo.id} value={prestamo.id}>
                                                {prestamo.cliente_nombre} - {formatCurrency(prestamo.saldo_pendiente)} pendiente
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            {selectedPrestamo && (
                                <Card className="bg-muted/50">
                                    <CardContent className="p-4">
                                        <div className="grid grid-cols-2 gap-4 text-sm">
                                            <div>
                                                <p className="text-muted-foreground">Monto Original</p>
                                                <p className="font-mono font-bold">{formatCurrency(selectedPrestamo.monto)}</p>
                                            </div>
                                            <div>
                                                <p className="text-muted-foreground">Saldo Pendiente</p>
                                                <p className="font-mono font-bold text-primary">{formatCurrency(selectedPrestamo.saldo_pendiente)}</p>
                                            </div>
                                            <div>
                                                <p className="text-muted-foreground">Cuotas Restantes</p>
                                                <p className="font-mono">{selectedPrestamo.cuotas.filter(c => c.estado !== 'pagada').length}</p>
                                            </div>
                                            <div>
                                                <p className="text-muted-foreground">Mínimo a Pagar</p>
                                                <p className="font-mono font-bold text-accent">{formatCurrency(getMinimoPago())}</p>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="monto">Monto a Pagar</Label>
                                    <Input
                                        id="monto"
                                        type="number"
                                        value={form.monto}
                                        onChange={(e) => setForm({ ...form, monto: e.target.value })}
                                        required
                                        min="1"
                                        step="0.01"
                                        data-testid="pago-monto-input"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <Label htmlFor="forma">Forma de Pago</Label>
                                    <Select
                                        value={form.forma_pago}
                                        onValueChange={(value) => setForm({ ...form, forma_pago: value })}
                                    >
                                        <SelectTrigger data-testid="pago-forma-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="efectivo">Efectivo</SelectItem>
                                            <SelectItem value="transferencia">Transferencia</SelectItem>
                                            <SelectItem value="otro">Otro</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <Label htmlFor="notas">Notas (opcional)</Label>
                                <Input
                                    id="notas"
                                    value={form.notas}
                                    onChange={(e) => setForm({ ...form, notas: e.target.value })}
                                    placeholder="Agregar nota..."
                                />
                            </div>

                            <div className="flex justify-end gap-2 pt-4">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                    Cancelar
                                </Button>
                                <Button type="submit" disabled={!form.prestamo_id || !form.monto} data-testid="pago-submit-btn">
                                    Registrar Pago
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Table */}
            <Card>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Fecha</TableHead>
                                    <TableHead>Monto</TableHead>
                                    <TableHead className="hidden md:table-cell">Distribución</TableHead>
                                    <TableHead className="hidden lg:table-cell">Forma</TableHead>
                                    <TableHead className="hidden lg:table-cell">Registrado por</TableHead>
                                    <TableHead className="w-12"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pagos.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-12">
                                            <Receipt className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                            <p className="text-muted-foreground">No hay pagos registrados</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    pagos.map((pago) => (
                                        <TableRow key={pago.id} className="animate-fadeIn">
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium">{pago.fecha.split('T')[0]}</p>
                                                    <p className="text-xs text-muted-foreground font-mono">
                                                        {pago.id.slice(0, 8).toUpperCase()}
                                                    </p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <p className="font-mono font-bold text-primary">
                                                    {formatCurrency(pago.monto)}
                                                </p>
                                            </TableCell>
                                            <TableCell className="hidden md:table-cell">
                                                <div className="flex gap-2 flex-wrap">
                                                    {pago.distribucion.capital > 0 && (
                                                        <Badge variant="outline" className="text-xs">
                                                            Cap: {formatCurrency(pago.distribucion.capital)}
                                                        </Badge>
                                                    )}
                                                    {pago.distribucion.interes > 0 && (
                                                        <Badge variant="outline" className="text-xs bg-primary/5">
                                                            Int: {formatCurrency(pago.distribucion.interes)}
                                                        </Badge>
                                                    )}
                                                    {pago.distribucion.mora > 0 && (
                                                        <Badge variant="outline" className="text-xs bg-destructive/10 text-destructive">
                                                            Mora: {formatCurrency(pago.distribucion.mora)}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </TableCell>
                                            <TableCell className="hidden lg:table-cell capitalize">
                                                {pago.forma_pago}
                                            </TableCell>
                                            <TableCell className="hidden lg:table-cell">
                                                {pago.registrado_por}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => downloadRecibo(pago.id)}
                                                        data-testid={`download-recibo-${pago.id}`}
                                                        title="Descargar recibo"
                                                    >
                                                        <Download className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => printRecibo(pago.id)}
                                                        data-testid={`print-recibo-${pago.id}`}
                                                        title="Imprimir recibo"
                                                    >
                                                        <Printer className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
